import { drumHitsAtStep, drumPatternLength } from './drumPattern';
import { chordRunsInMeasure } from './chordAccompaniment';
import { resolveVoicing } from './chordVoicing';
import { flattenPlayback } from './pattern';
import type { ChordAccompaniment, ChordId, DrumId, DrumPattern, NoteId, Pattern } from '../types';

export interface SoundEvent {
  timeSec: number;
  kind: 'note' | 'drum' | 'chord';
  note?: NoteId;
  drum?: DrumId;
  chord?: ChordId;
  durationMs?: number;
  holdSec?: number;
}

export interface UiEvent {
  timeSec: number;
  kind: 'melody' | 'drum';
  globalIndex?: number;
  measureIndex?: number;
  drumStep?: number;
}

export interface PlaybackSchedule {
  sounds: SoundEvent[];
  ui: UiEvent[];
  totalDurationSec: number;
}

export function buildPlaybackSchedule(
  pattern: Pattern,
  bpm: number,
  drumPattern: DrumPattern | null,
  chordAccompaniment: ChordAccompaniment | null = null,
): PlaybackSchedule {
  const melodyEvents = flattenPlayback(pattern, bpm);
  const sounds: SoundEvent[] = [];
  const ui: UiEvent[] = [];

  let timeSec = 0;
  for (const event of melodyEvents) {
    ui.push({
      timeSec,
      kind: 'melody',
      globalIndex: event.globalIndex,
      measureIndex: event.measureIndex,
    });
    if (event.cell.note !== 'rest') {
      sounds.push({
        timeSec,
        kind: 'note',
        note: event.cell.note,
        durationMs: event.durationMs * 0.92,
      });
    }
    timeSec += event.durationMs / 1000;
  }

  const totalDurationSec = timeSec;

  const measureStarts: number[] = [];
  const measureEnds: number[] = [];
  let boundaryTime = 0;
  let lastMeasure = -1;
  for (const event of melodyEvents) {
    if (event.measureIndex !== lastMeasure) {
      if (lastMeasure >= 0) measureEnds[lastMeasure] = boundaryTime;
      measureStarts[event.measureIndex] = boundaryTime;
      lastMeasure = event.measureIndex;
    }
    boundaryTime += event.durationMs / 1000;
  }
  if (lastMeasure >= 0) measureEnds[lastMeasure] = boundaryTime;

  const stepSec = 60 / bpm / 4;

  if (chordAccompaniment?.enabled && totalDurationSec > 0) {
    for (let mi = 0; mi < pattern.measures.length; mi++) {
      const mStart = measureStarts[mi] ?? 0;
      for (const run of chordRunsInMeasure(chordAccompaniment, mi)) {
        const stepTime = mStart + run.startStep * stepSec;
        const steps = run.endStep - run.startStep + 1;
        sounds.push({
          timeSec: stepTime,
          kind: 'chord',
          chord: run.chordId,
          chordFrequencies: resolveVoicing(chordAccompaniment, run.chordId),
          holdSec: steps * stepSec * 0.98,
        });
      }
    }
  }

  if (drumPattern?.enabled && totalDurationSec > 0) {
    const totalSteps = drumPatternLength(drumPattern.measures);
    let drumTime = 0;
    let step = 0;

    while (drumTime < totalDurationSec - 1e-9) {
      const drumStep = step % totalSteps;
      ui.push({ timeSec: drumTime, kind: 'drum', drumStep });
      for (const drum of drumHitsAtStep(drumPattern, drumStep)) {
        sounds.push({ timeSec: drumTime, kind: 'drum', drum });
      }
      drumTime += stepSec;
      step += 1;
    }
  }

  ui.sort((a, b) => a.timeSec - b.timeSec || (a.kind === 'drum' ? 1 : -1));

  return { sounds, ui, totalDurationSec };
}
